import React, { useState, useEffect } from "react";
import {Typography,Box,Skeleton,} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { createComment, getCommentsByScamId, getSuggestedScams, searchScamByPostId, updateReaction } from "src/api_endpoints/userDashboard/ScamAPI";
import { useDispatch, useSelector } from "react-redux";
import SearchBar from "./SearchBar";
import { dislikeScam, incCommentCount, likeScam, neutralScam } from "src/redux/scam/scamActions";
import ViewScamImagesModal from "./viewScamImagesModal";
import ScamAlertCommonCard from "./ScamAlertCommonCard";
import ViewCommentsModal from "./ViewCommentsModal";

const SuggestedScams = () => {
  const { id: userId, first_name, last_name, user_avatar } = useSelector((state) => state.userState?.userData);
  const dispatch = useDispatch();
  const [scams, setScams] = useState([]);
  const [scamId, setScamId] = useState(null);
  const [scamCode, setScamCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [currentImages, setCurrentImages] = useState([]);
  const [openCommentsModal, setOpenCommentsModal] = useState(false);
  const [currentScamComments, setCurrentScamComments] = useState([]);


  const fetchScams = async () => {
    try {
      setLoading(true)
      const response = await getSuggestedScams({user_id:userId});
      // console.log('getSuggestedScams', response)
      setScams(Array.isArray(response.scams) ? response.scams : []);
      setLoading(false)

    } catch (error) {
      setLoading(false)
      setError("Failed to load scam reports. Please try again later.");
    }
  };


  const handleOpenCommentsModal = async (scm) => {
    try {
      const scam_id = scm.scamPK;
      const scam_code = scm.scam_code

      if (!scam_id) {
        throw new Error("Invalid scam ID provided");
      }

      const res = await getCommentsByScamId({ scam_id, user_id: userId });
      setScamId(scam_id)
      setScamCode(scam_code)
      if (res) {
        setCurrentScamComments(res.comments);
        setOpenCommentsModal(true);
      } else {
        throw new Error("Failed to fetch comments or no comments found.");
      }
    } catch (error) {
      console.error("Error fetching comments:", error.message);
    }
  };

  const handleCreateComment = async (comment, scamId) => {
    try {
      const res = await createComment({
        user_id: userId,
        scam_id: scamId,
        comment
      });
      if (res) {
        dispatch(incCommentCount(scamId))
        setCurrentScamComments((prevComments) => [
          ...prevComments,
          {
            user_id: userId,
            comment: comment,
            status: 1, // Assuming the new comment is active by default
            date_created: new Date().toISOString(), // Current timestamp in ISO format
            first_name, // Replace with dynamic values
            last_name,
            user_avatar,
            id: res.commentId
          }
        ]);
      } else {
        throw new Error(res.message || "Failed to create comment");
      }
    } catch (error) {
      console.error("Error creating comment:", error.message);
    }
  };

  const handleCloseCommentsModal = () => {
    setOpenCommentsModal(false);
  };

  const handleSearch = async () => {
    if (!searchInput.trim()) {
      try {
        setLoading(true);
        const response = await getSuggestedScams({user_id:userId});
        setScams(Array.isArray(response.scams) ? response.scams : []);
        setLoading(false);
      } catch (error) {
        setError("Failed to reload scam reports. Please try again later.");
        setLoading(false);
      }
      return;
    }
    try {
      setLoading(true);
      const response = await searchScamByPostId({ postId: searchInput, userId });
      setScams(Array.isArray(response) ? response : []);
      setLoading(false);
    } catch (error) {
      setError("Failed to find the post. Please try again.");
      setLoading(false);
    }
  };

  const handleReaction = async (scamId, newReaction) => {
    try {
      const formData = new FormData();
      formData.append("scam_code", scamId);
      formData.append("user_id", userId);
      formData.append("reaction_type", newReaction);

      await updateReaction(formData);

      setScams(prevScams =>
        prevScams.map(scam => {
          if (scam.scamPK === scamId) {
            let updatedLikes = scam.total_likes;
            let updatedDislikes = scam.total_dislikes;

            // Remove previous reaction effect
            if (scam.reaction_type === 1) updatedLikes--;
            if (scam.reaction_type === 2) updatedDislikes--;

            // Apply new reaction
            if (newReaction === 1) updatedLikes++;
            if (newReaction === 2) updatedDislikes++;

            return {
              ...scam,
              reaction_type: newReaction === 'null' ? null : newReaction,
              total_likes: updatedLikes,
              total_dislikes: updatedDislikes,
              isLoggedInUserLiked: newReaction === 1,
              isLoggedInUserDisliked: newReaction === 2,
            };
          }
          return scam;
        })
      );

      // Dispatch global state updates if necessary
      if (newReaction === 1) {
        dispatch(likeScam(scamId));
      } else if (newReaction === 2) {
        dispatch(dislikeScam(scamId));
      } else {
        dispatch(neutralScam(scamId));
      }

    } catch (err) {
      console.error("Failed to update reaction:", err);
      setError("An error occurred while updating your reaction.");
    }
  };

  const handleOpenModal = (images) => {
    setCurrentImages(images);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleToggleLikeInParent = (index, scam_comment_id, isLikedComment, totalLikesOnReplyComment) => {
    // Clone current comments to update the specific comment
    const updatedComments = [...currentScamComments];

    // Toggle the like status and update the like count
    updatedComments[index].isLikedComment = !isLikedComment;
    updatedComments[index].totalLikesOnReplyComment = isLikedComment
      ? totalLikesOnReplyComment - 1
      : totalLikesOnReplyComment + 1;
    setCurrentScamComments(updatedComments); // Update state to reflect the changes
  };

  useEffect(() => {
    fetchScams();
  }, [userId]);


  if (loading) {
    return (
      <Box sx={{ padding: "1rem" }}>
        <Skeleton variant="rectangular" width="100%" height={200} sx={{ marginBottom: 2 }} />
        <Skeleton variant="text" width="60%" sx={{ marginBottom: 1 }} />
        <Skeleton variant="text" width="40%" sx={{ marginBottom: 1 }} />
        <Skeleton variant="text" width="100%" sx={{ marginBottom: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ padding: "1rem", textAlign: "center" }}>
        {error}
      </Typography>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" className="pl-5" sx={{
            color: '#f44336',
            paddingRight: '.4rem !important',
            padding: { xs: 0, sm: '16px' },
            margin: { xs: '.5rem', sm: '0' },
          }}>
            Scam alerts
          </Typography>
          <Typography sx={{ marginBottom: '-3px' }}>
            <WarningIcon color="error" />
          </Typography>
        </Box>

        <SearchBar searchInput={searchInput} setSearchInput={setSearchInput} handleSearch={handleSearch} />
     
      </Box>
      {!loading && Array.isArray(scams) && scams.length === 0 ? (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ padding: "1rem", textAlign: "center", marginTop: '2rem' }}
        >
          No scam post found.
        </Typography>
      ) : (
        Array.isArray(scams) &&
        scams.map((scam, index) => (
          <ScamAlertCommonCard
            key={index}
            scam={scam}
            onReaction={handleReaction}
            onOpenModal={handleOpenModal}
            onCloseModal={handleCloseModal}
            openModal={openModal}
            isViewMyReports={false}  // Pass false here to control rendering
            handleOpenCommentsModal={handleOpenCommentsModal}
          />
        ))
      )}
      <ViewScamImagesModal
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        currentImages={currentImages}
      />
      <ViewCommentsModal
        scamId={scamId}
        scamCode={scamCode}
        commentData={currentScamComments}  // Pass current comments
        open={openCommentsModal}
        handleClose={handleCloseCommentsModal}
        handleCreateComment={handleCreateComment}
        handleToggleLikeInParent={handleToggleLikeInParent}
      />
    </>
  );
};

export default SuggestedScams;
